import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { AlbumActivity } from "@family/shared";
import { Avatar } from "./Avatar";
import { formatDate } from "../lib/dates";

function uploaderLine(item: AlbumActivity): string {
  const [first, ...rest] = item.uploaders;
  const name = first?.displayName ?? "Someone";
  if (rest.length === 0) return name;
  return `${name} and ${rest.length} other${rest.length === 1 ? "" : "s"}`;
}

/**
 * Home-feed card for "photos were added to an album or event", shaped like a
 * post: who added them, a swipeable carousel of the new photos, and a way
 * into the album or event. It is built from the album/event on every read,
 * not stored as a post, so it can't drift from what that actually holds.
 */
export function AlbumActivityCard({ item }: { item: AlbumActivity }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  // Slide a button press is scrolling to, until the scroll arrives there.
  const targetRef = useRef<number | null>(null);
  const isEvent = item.kind === "event";
  const albumUrl = isEvent ? `/events/${item.targetId}` : `/albums/${item.targetId}`;
  const place = isEvent ? "event" : "album";
  const hiddenCount = item.photoCount - item.media.length;
  // One extra slide links to the rest of the album when the carousel is capped.
  const slideCount = item.media.length + (hiddenCount > 0 ? 1 : 0);
  const noun = item.photoCount === 1 ? "photo" : "photos";

  function onScroll() {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const at = Math.round(el.scrollLeft / el.clientWidth);
    if (at === targetRef.current) targetRef.current = null;
    setIndex(at);
  }

  // Step from the slide already being scrolled to, so quick taps during the
  // smooth scroll each move one more photo instead of repeating the same one.
  function step(delta: number) {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const current = targetRef.current ?? Math.round(el.scrollLeft / el.clientWidth);
    const next = Math.max(0, Math.min(slideCount - 1, current + delta));
    targetRef.current = next;
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
  }

  return (
    <li className="card album-activity" style={{ marginBottom: "var(--s-4)", padding: "var(--s-4)" }}>
      {/* Author row, matching posts */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)", marginBottom: "var(--s-3)" }}>
        <Avatar
          src={item.uploaders[0]?.avatarUrl ?? null}
          name={item.uploaders[0]?.displayName ?? null}
          size={40}
        />
        <div style={{ lineHeight: 1.3, minWidth: 0 }}>
          <span style={{ fontSize: "0.95rem" }}>
            <strong>{uploaderLine(item)}</strong> added {item.photoCount} {noun} to{" "}
            <Link to={albumUrl} style={{ fontWeight: 600 }}>
              {item.title}
            </Link>
          </span>
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--c-text-subtle)",
            }}
          >
            {isEvent ? "Event photos" : "Album"} · {formatDate(item.addedAt)}
          </div>
        </div>
      </div>

      {slideCount > 0 && (
        <div className="album-activity__carousel">
          <div
            ref={trackRef}
            className="album-activity__track"
            onScroll={onScroll}
            aria-label={`${item.media.length} new ${noun} in ${item.title}`}
          >
            {item.media.map((m, i) => (
              <Link
                key={m.id}
                to={albumUrl}
                className="album-activity__slide"
                aria-label={`Photo ${i + 1} of ${item.photoCount}, open ${place}`}
              >
                <img
                  src={m.kind === "video" ? (m.thumbnailUrl ?? undefined) : m.viewUrl}
                  alt=""
                  loading={i === 0 ? "eager" : "lazy"}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
                {m.kind === "video" && <span className="album-activity__play" aria-hidden="true">▶</span>}
              </Link>
            ))}
            {hiddenCount > 0 && (
              <Link to={albumUrl} className="album-activity__slide album-activity__more">
                +{hiddenCount} more in the {place}
              </Link>
            )}
          </div>

          {slideCount > 1 && (
            <>
              <button
                type="button"
                className="album-activity__nav album-activity__nav--prev"
                aria-label="Previous photo"
                onClick={() => step(-1)}
                disabled={index === 0}
              >
                ‹
              </button>
              <button
                type="button"
                className="album-activity__nav album-activity__nav--next"
                aria-label="Next photo"
                onClick={() => step(1)}
                disabled={index >= slideCount - 1}
              >
                ›
              </button>
              <span className="album-activity__count">
                {Math.min(index + 1, slideCount)} / {slideCount}
              </span>
            </>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "var(--s-2)",
          marginTop: "var(--s-3)",
          paddingTop: "var(--s-2)",
          borderTop: "1px solid var(--c-border)",
        }}
      >
        <Link to={albumUrl} className="post-action-btn" style={{ textDecoration: "none" }}>
          Open {place}
        </Link>
      </div>
    </li>
  );
}
